import { useCallback, useMemo, useState } from 'react';

import { confirmDialog } from 'primereact/confirmdialog';

import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  donorSchedulesApi,
  useDeactivateDonorSchedulesMutation,
} from '@/app/services/donorSchedules';
import { DonorSchedulingActions, PartnershipsActions } from '@/app/slices';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { LOAD_DONOR_PARTNERS_OPTION_VALUES } from '@/enums';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  type DonorSchedulePastDueInput,
  getClosedNoNewBusinessValue,
  getDonorSchedulePastDueStatus,
  getPastDueStatus,
  type PastDuePartnership,
} from '@/hooks/usePastDueStatus';
import { DonorSchedule, Partnership } from '@/rtk-query.gql';
import { useReactivateDonorSchedules } from '@/services/donor-scheduling';
import { useMassSetField } from '@/services/donor-scheduling/hooks';
import {
  useDonorScheduleExcelReport,
  useDonorSchedulePdfReport,
  useDonorToDoListExcelReport,
  useDonorToDoListPdfReport,
} from '@/services/reports';
import {
  runNewWorkflow,
  WORKFLOW,
  Workflow as WorkflowType,
} from '@/utils/workflow';

import { DonorToDoReportFormDataOutput } from '../components/toolbar/schema';
import { CLEARABLE_FIELDS } from '../constants';
import { ClearableField } from '../types';

export const getPartnershipsForBillingCheck = (
  partnerships: Partnership[],
  billingAccountId: string | null | undefined,
  isBillingPartnership: boolean,
): PastDuePartnership[] => {
  if (isBillingPartnership) {
    return partnerships as unknown as PastDuePartnership[];
  }

  if (!billingAccountId) {
    return [];
  }

  for (const p of partnerships) {
    const accountSlots = [
      { id: p.account1Id, account: p.account1 },
      { id: p.account2Id, account: p.account2 },
      { id: p.account3Id, account: p.account3 },
      { id: p.account4Id, account: p.account4 },
    ];

    const match = accountSlots.find(({ id }) => id === billingAccountId);
    if (match?.account) {
      return [
        {
          account1: match.account as any,
          account2: null,
          account3: null,
          account4: null,
        },
      ];
    }
  }

  return [];
};

export type ValidationIssue = {
  donorScheduleId: string;
  donorScheduleName: string;
  issues: string[];
  isLocked?: boolean;
};

type WorkflowValidationResult = {
  isValid: boolean;
  message?: string;
  validationIssues?: ValidationIssue[];
};

export const useContextMenuActions = () => {
  const dispatch = useAppDispatch();
  const { selectedDonorSchedules: appSelectedDonorSchedules } = useAppSelector(
    (state) => state.donorScheduling,
  );

  const [clearFieldDialogVisible, setClearFieldDialogVisible] = useState(false);
  const [selectedField, setSelectedField] = useState<ClearableField | null>(
    null,
  );
  const [reportDialogVisible, setReportDialogVisible] = useState<
    'pdf' | 'excel' | undefined
  >();
  const [missingFieldsDialogVisible, setMissingFieldsDialogVisible] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pendingWorkflow, setPendingWorkflow] = useState<WorkflowType | null>(
    null,
  );

  const isReactivateSchedulesEnabled = useFeatureFlag(
    'DS_Reactivate_Schedules',
  );
  const isBillingAccountPastDueEnabled = useFeatureFlag(
    'DS_Past_Due_Billing_Account',
  );

  const [deactivateDonorSchedule] = useDeactivateDonorSchedulesMutation();
  const reactivateDonorSchedules = useReactivateDonorSchedules();
  const { mutateAsync: massSetField } = useMassSetField();
  const workflows = [
    WORKFLOW.Rollover2Week,
    WORKFLOW.Rollover4Week,
    WORKFLOW.SOSchedule,
    WORKFLOW.Split,
  ];

  const handleRefreshData = useCallback(() => {
    dispatch(donorSchedulesApi.util.invalidateTags(['DonorSchedules']));
    dispatch({
      type: DonorSchedulingActions.INCREMENT_REFETCH_TRIGGER,
    });
  }, [dispatch]);

  const handleDeactivate = useCallback(async () => {
    try {
      const donorScheduleIds = appSelectedDonorSchedules?.map(
        (schedule: DonorSchedule) => schedule.id,
      );
      await deactivateDonorSchedule({ donorScheduleIds })
        .unwrap()
        .then(() => {
          handleRefreshData();
          dispatch({
            type: DonorSchedulingActions.RESET_SELECTED_DONOR_SCHEDULING,
          });
          dispatch({
            type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
          });
          dispatch({
            type: PartnershipsActions.RESET_PARTNERSHIP,
          });
        });
    } catch (error) {
      console.error('Failed to Deactivate donor schedules:', error);
    }
  }, [
    appSelectedDonorSchedules,
    deactivateDonorSchedule,
    handleRefreshData,
    dispatch,
  ]);

  // Inactive schedules only: an all-active selection is a no-op, mirroring
  // how Deactivate behaves on already-inactive schedules.
  const inactiveSelectedSchedules = useMemo(
    () =>
      appSelectedDonorSchedules.filter(
        (schedule: DonorSchedule) => schedule.stateCode === 1,
      ),
    [appSelectedDonorSchedules],
  );

  const handleReactivate = useCallback(async () => {
    if (inactiveSelectedSchedules.length === 0) return;

    try {
      const donorScheduleIds = inactiveSelectedSchedules.map(
        (schedule: DonorSchedule) => schedule.id,
      );
      await reactivateDonorSchedules.mutateAsync(donorScheduleIds);

      handleRefreshData();
      dispatch({
        type: DonorSchedulingActions.RESET_SELECTED_DONOR_SCHEDULING,
      });
      dispatch({
        type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
      });
      dispatch({
        type: PartnershipsActions.RESET_PARTNERSHIP,
      });
    } catch (error) {
      console.error('Failed to Reactivate donor schedules:', error);
    }
  }, [
    inactiveSelectedSchedules,
    reactivateDonorSchedules,
    handleRefreshData,
    dispatch,
  ]);

  const handleReactivateWithConfirmation = useCallback(() => {
    if (inactiveSelectedSchedules.length === 0) return;

    confirmDialog({
      message: (
        <div className="flex flex-col gap-3">
          <p className="m-0">
            {inactiveSelectedSchedules.length} selected schedule
            {inactiveSelectedSchedules.length === 1 ? '' : 's'} will be
            reactivated
          </p>
          <ul className="m-0 max-h-60 list-disc overflow-y-auto pl-6">
            {inactiveSelectedSchedules.map((schedule: DonorSchedule) => (
              <li key={schedule.id}>{schedule.name}</li>
            ))}
          </ul>
          <p className="m-0">Are you sure you want to continue?</p>
        </div>
      ),
      header: 'Reactivate Donor Schedules',
      icon: 'pi pi-exclamation-triangle',
      accept: handleReactivate,
      acceptLabel: 'Yes',
      rejectLabel: 'No',
    });
  }, [inactiveSelectedSchedules, handleReactivate]);

  const handleClearField = useCallback(
    async (fieldToClear: ClearableField | null) => {
      if (!fieldToClear) return;

      try {
        const donorScheduleIds = appSelectedDonorSchedules?.map(
          (schedule: DonorSchedule) => schedule.id,
        );
        await massSetField({
          donorScheduleIds,
          fields: [fieldToClear],
          value: '',
        });
        handleRefreshData();
        dispatch({
          type: DonorSchedulingActions.RESET_WORKING_DONOR_SCHEDULE,
        });
        dispatch({
          type: PartnershipsActions.RESET_PARTNERSHIP,
        });
      } catch (error) {
        console.error('Failed to mass clear field:', error);
      }
    },
    [appSelectedDonorSchedules, massSetField, handleRefreshData, dispatch],
  );

  const openClearFieldDialog = useCallback(() => {
    setClearFieldDialogVisible(true);
  }, []);

  const openMissingFieldsDialog = useCallback(() => {
    setMissingFieldsDialogVisible(true);
  }, []);

  const [donorSchedulePdf] = useDonorSchedulePdfReport();
  const [donorScheduleExcel] = useDonorScheduleExcelReport();
  const handlePrintTogDonorScheduleReport = useCallback(
    async (type: 'pdf' | 'excel') => {
      try {
        setIsLoading(true);

        const soScheduleIds = appSelectedDonorSchedules
          .map((donorSchedule: DonorSchedule) => donorSchedule?.soSchedule?.id)
          .filter(Boolean) as string[];

        if (type === 'pdf') {
          await donorSchedulePdf({ soScheduleIds });
        } else {
          await donorScheduleExcel({ soScheduleIds });
        }
      } catch (error) {
        console.error('Error creating fresh shipment report:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [appSelectedDonorSchedules, donorScheduleExcel, donorSchedulePdf],
  );

  const [donorToDoListPdf] = useDonorToDoListPdfReport();
  const [donorToDoListExcel] = useDonorToDoListExcelReport();
  const handlePrintDonorToDoReport = useCallback(
    async (params: DonorToDoReportFormDataOutput) => {
      const reportType = reportDialogVisible;
      if (!reportType) {
        return;
      }

      try {
        setIsLoading(true);

        if (reportType === 'pdf') {
          await donorToDoListPdf(params);
        } else {
          await donorToDoListExcel(params);
        }
      } catch (error) {
        console.error('Error creating fresh shipment report:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [donorToDoListExcel, donorToDoListPdf, reportDialogVisible],
  );

  const workflowValidation = useCallback(
    (workflow: WorkflowType): WorkflowValidationResult => {
      if (workflow.label === 'SO Schedule') {
        const validationIssues: ValidationIssue[] = [];
        const closedNoNewBusinessValue = getClosedNoNewBusinessValue();

        appSelectedDonorSchedules.forEach((donorSchedule: DonorSchedule) => {
          const issues: string[] = [];

          const isBillingPartnership =
            donorSchedule.loadDonorPartners ===
            LOAD_DONOR_PARTNERS_OPTION_VALUES.YES;

          const partnershipsToCheck = getPartnershipsForBillingCheck(
            donorSchedule.partnerships ?? [],
            donorSchedule.billingAccountId,
            isBillingPartnership,
          );

          const { hasOver90Days, isLocked, overrideActiveUntil } =
            isBillingAccountPastDueEnabled
              ? getDonorSchedulePastDueStatus(
                  donorSchedule as unknown as DonorSchedulePastDueInput,
                )
              : getPastDueStatus(
                  donorSchedule.owner as any,
                  partnershipsToCheck,
                  closedNoNewBusinessValue,
                );

          if (hasOver90Days) {
            if (isLocked) {
              issues.push(
                'Account or partner is over 90 days and is restricted/closed',
              );
            } else if (overrideActiveUntil) {
              issues.push(
                'Account or partner is over 90 days but override is active until ' +
                  overrideActiveUntil,
              );
            } else {
              issues.push('Account or partner is over 90 days');
            }
          }

          const hasPartnershipAccounts =
            donorSchedule.partnerships &&
            donorSchedule.partnerships.some(
              (p) =>
                p.account1Id || p.account2Id || p.account3Id || p.account4Id,
            );

          if (isBillingPartnership && !hasPartnershipAccounts) {
            issues.push('Partnership selected but not entered');
          }

          if (!donorSchedule.scheduleMasterId) {
            issues.push('Schedule master is blank');
          }

          const exportField = donorSchedule.export;
          if (exportField === null || exportField === undefined) {
            issues.push('Export field is blank');
          }

          if (issues.length > 0) {
            validationIssues.push({
              donorScheduleId: donorSchedule.id || '',
              donorScheduleName: donorSchedule.name || '',
              issues,
              isLocked,
            });
          }
        });

        if (validationIssues.length > 0) {
          return {
            isValid: false,
            message:
              'Some donor schedules have validation issues that will prevent SO schedule from running.',
            validationIssues,
          };
        }
      }
      return { isValid: true };
    },
    [appSelectedDonorSchedules, isBillingAccountPastDueEnabled],
  );

  const handleWorkflowExecute = useCallback(
    async (
      workflow: WorkflowType,
      onSoScheduleWarning?: (validationIssues: ValidationIssue[]) => void,
    ) => {
      if (workflowValidation) {
        const validation = workflowValidation(workflow);

        if (!validation.isValid) {
          if (
            workflow.label === 'SO Schedule' &&
            validation.validationIssues &&
            onSoScheduleWarning
          ) {
            setPendingWorkflow(workflow);
            onSoScheduleWarning(validation.validationIssues);
            return;
          }

          const issueDetail =
            validation.validationIssues &&
            validation.validationIssues.length > 0
              ? validation.validationIssues
                  .map((v) => `${v.donorScheduleName}: ${v.issues.join(', ')}`)
                  .join('\n')
              : validation.message || 'This workflow cannot be executed.';

          dispatch(
            addToast({
              severity: 'error',
              summary: 'Workflow Restriction',
              detail: issueDetail,
              life: ToastLife.LONG,
            }),
          );
          return;
        }
      }

      try {
        const recordIds = appSelectedDonorSchedules.map(
          (donorSchedule: DonorSchedule) => ({
            RecordId: donorSchedule.id,
          }),
        );

        await runNewWorkflow(recordIds, workflow.id);

        dispatch(
          addToast({
            severity: 'success',
            summary: 'Workflow Executed',
            detail: `${workflow.label} workflow completed successfully. Workflows don't run right away. Please refresh the page to see how they are being created.`,
            life: ToastLife.DEFAULT,
          }),
        );
        handleRefreshData();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Workflow Error',
            detail: `Failed to execute ${workflow.label} workflow. Please try again. Error: ${errorMessage}`,
            life: ToastLife.LONG,
          }),
        );
      }
    },
    [
      workflowValidation,
      appSelectedDonorSchedules,
      dispatch,
      handleRefreshData,
    ],
  );

  const handleSoScheduleWarningAccept = useCallback(async () => {
    if (!pendingWorkflow) {
      console.error('No pending workflow to execute');
      return;
    }

    try {
      const recordIds = appSelectedDonorSchedules.map(
        (donorSchedule: DonorSchedule) => ({
          RecordId: donorSchedule.id,
        }),
      );
      await runNewWorkflow(recordIds, pendingWorkflow.id);
      dispatch(
        addToast({
          severity: 'success',
          summary: 'Workflow Executed',
          detail: `${pendingWorkflow.label} workflow completed successfully.`,
          life: ToastLife.DEFAULT,
        }),
      );
      handleRefreshData();
      setPendingWorkflow(null);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      dispatch(
        addToast({
          severity: 'error',
          summary: 'Workflow Error',
          detail: `Failed to execute ${pendingWorkflow.label} workflow. Please try again. Error: ${errorMessage}`,
          life: ToastLife.LONG,
        }),
      );
      console.error('Workflow error:', error);
    }
  }, [appSelectedDonorSchedules, dispatch, handleRefreshData, pendingWorkflow]);

  const handleDeactivateWithConfirmation = useCallback(() => {
    const scheduleNames = appSelectedDonorSchedules
      .map((schedule: DonorSchedule) => schedule.name)
      .join(', ');

    confirmDialog({
      message: isReactivateSchedulesEnabled ? (
        <div className="flex flex-col gap-3">
          <p className="m-0">
            {appSelectedDonorSchedules.length} selected schedule
            {appSelectedDonorSchedules.length === 1 ? '' : 's'} will be
            deactivated
          </p>
          <ul className="m-0 max-h-60 list-disc overflow-y-auto pl-6">
            {appSelectedDonorSchedules.map((schedule: DonorSchedule) => (
              <li key={schedule.id}>{schedule.name}</li>
            ))}
          </ul>
          <p className="m-0">Are you sure you want to continue?</p>
        </div>
      ) : (
        `Are you sure you want to deactivate ${scheduleNames}? This action will deactivate the selected donor schedules and remove their associated SO schedules and aspirations.`
      ),
      header: 'Deactivate Donor Schedules',
      icon: 'pi pi-exclamation-triangle',
      accept: handleDeactivate,
      acceptLabel: 'Yes',
      rejectLabel: 'No',
    });
  }, [
    appSelectedDonorSchedules,
    handleDeactivate,
    isReactivateSchedulesEnabled,
  ]);

  return {
    clearFieldDialogVisible,
    setClearFieldDialogVisible,
    selectedField,
    setSelectedField,
    reportDialogVisible,
    setReportDialogVisible,
    missingFieldsDialogVisible,
    setMissingFieldsDialogVisible,
    isLoading,
    appSelectedDonorSchedules,
    workflows,
    CLEARABLE_FIELDS,
    pendingWorkflow,

    handleDeactivate,
    handleDeactivateWithConfirmation,
    handleReactivate,
    handleReactivateWithConfirmation,
    isReactivateSchedulesEnabled,
    handleClearField,
    handlePrintTogDonorScheduleReport,
    handlePrintDonorToDoReport,
    handleWorkflowExecute,
    handleSoScheduleWarningAccept,
    handleRefreshData,
    openClearFieldDialog,
    openMissingFieldsDialog,
  };
};
